import { useState, useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    GridActionsCellItem,
    GridColDef,
    GridRowId,
    GridPaginationModel,
    GridSortModel,
    GridRenderCellParams,
    GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import localforage from 'localforage';
import BasePaper from '@/components/ui/BasePaper';
import BaseCard from '@/components/ui/BaseCard';
import BaseDataTable from '@/components/ui/BaseDataTable';
import BaseTitle from '@/components/ui/BaseTitle';
import { Create, Delete, LocationOn, RoomPreferences } from '@mui/icons-material';
import { Chip, Box } from '@mui/material';
import BaseButton from '@/components/ui/BaseButton';
import BaseDialog from '@/components/ui/BaseDialog';
import {
    AdminStoresPageState,
    AdminStoresPageAction,
    Store as StoreType,
    StoreCluster,
    StoreLocation,
} from '@/types/pages/admin/stores';
import { fetchStoreListAPI, deleteStoreAPI, deleteStoreConfirmAPI } from '@/api/admin/stores';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '@/provider/contexts';

import '@/styles/admin.scss';
import CaptchaCanvas from '@/components/form/CaptchaCanvas';
import SearchBar from '@/components/form/SearchBar';
import { getInputMinLengthLimit } from '@/utils/inputUtils';
import { useTranslation } from 'react-i18next';
import BaseTableColumn from '@/components/ui/BaseTableColumn';

// interface tableRow {
//     id: string;
//     storeName: string;
//     info: string;
//     nimbus?: unknown;
//     action?: unknown;
// }

function reducer(state: AdminStoresPageState, action: AdminStoresPageAction) {
    switch (action.type) {
        case 'SET_TABLE_LOADING':
            return { ...state, tableLoading: action.payload };
        case 'SET_SEARCH_QUERY':
            return {
                ...state,
                searchQuery: action.payload,
            };
        case 'SET_PAGINATION_MODEL':
            return { ...state, paginationModel: action.payload };
        case 'SET_STORE_TABLE':
            return {
                ...state,
                tableLoading: false,
                storeList: action.payload.aaData,
                rowCount: action.payload.total_records,
            };
        case 'SET_CAPTCHA_KEY':
            return {
                ...state,
                captchaKey: action.payload,
            };
        case 'SET_CAPTCHA':
            return {
                ...state,
                captcha: action.payload,
            };
        case 'SET_IS_CAPTCHA_ERROR':
            return {
                ...state,
                isCaptchaError: action.payload,
            };
        case 'CLEAR_CAPTCHA':
            return {
                ...state,
                captchaKey: '',
                captcha: '',
                isCaptchaError: false,
            };
        case 'SET_SORT_MODEL':
            return {
                ...state,
                sortModel: action.payload,
            };
        default:
            return state;
    }
}

function AdminStorePage() {
    const { t } = useTranslation('common');
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deleteStoreId, setDeleteStoreId] = useState<number | null>(null);
    const [validatedMessage, setValidatedMessage] = useState<{ [key: string]: string | null }>({});
    const { setNotification, setManageStoreId } = useAppContext();

    const nav = useNavigate();

    const defaultState: AdminStoresPageState = {
        tableLoading: false,
        storeList: [],
        rowCount: 0,
        searchQuery: '',
        paginationModel: {
            page: 0,
            pageSize: 50,
        },
        captchaKey: '',
        captcha: '',
        isCaptchaError: false,
        sortModel: { field: null, sort: null },
    };

    const [
        {
            tableLoading,
            storeList,
            rowCount,
            searchQuery,
            paginationModel,
            sortModel,
            captchaKey,
            captcha,
            isCaptchaError,
        },
        dispatch,
    ] = useReducer(reducer, defaultState);
    const fetchAbortControllerRef = useRef<AbortController | null>(null);
    const latestFetchIdRef = useRef(0);

    const defaultPageParams = {
        current_page: paginationModel.page + 1,
        data_per_page: paginationModel.pageSize,
    };

    const getRowId = (row: StoreType) => {
        return row.store_id;
    };

    const editItem = useCallback(
        (id: GridRowId) => {
            nav('/admin/store/' + id);
        },
        [nav],
    );

    const deleteItem = useCallback(
        async (id: number) => {
            setDeleteStoreId(id);

            try {
                const {
                    data: { data, isSuccess, message },
                } = await deleteStoreAPI(id);
                if (isSuccess) {
                    setIsDeleteDialogOpen(true);
                    dispatch({ type: 'SET_CAPTCHA_KEY', payload: data.captcha });
                } else {
                    setNotification({ message: message, severity: 'error' });
                }
            } catch (error) {
                console.error('An unexpected error occurred:', error);
                setNotification({
                    message: t('An unexpected error occurred'),
                    severity: 'error',
                });
            }
        },
        [setNotification, t],
    );

    const renderSimpleCell = useCallback((params: GridRenderCellParams) => {
        const value = params.value;
        const displayValue = value === null || value === undefined || value === '' ? '-' : String(value);
        return <BaseTableColumn columnValue={displayValue} />;
    }, []);

    const columns: GridColDef[] = useMemo(
        () => [
            { field: 'store_id', headerName: t('Store ID'), flex: 1, renderCell: renderSimpleCell },
            {
                field: 'store_name',
                headerName: t('Store Name'),
                flex: 1,
                minWidth: 140,
                hideable: false,
                renderCell: renderSimpleCell,
            },
            {
                field: 'store_code',
                headerName: t('Code'),
                flex: 1,
                minWidth: 90,
                hideable: false,
                renderCell: renderSimpleCell,
            },
            {
                field: 'store_cluster',
                headerName: t('Cluster'),
                flex: 1,
                minWidth: 140,
                renderCell: (params) => {
                    const clusters = params.value as StoreCluster[] | undefined;
                    if (!clusters?.length) return <BaseTableColumn columnValue="-" />;

                    return (
                        <BaseTableColumn>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                    height: '100%',
                                }}
                            >
                                {clusters.map((cluster: StoreCluster) => (
                                    <Chip key={cluster.id} label={cluster.cluster_name} size="small" sx={{ mr: 0.2 }} />
                                ))}
                            </Box>
                        </BaseTableColumn>
                    );
                },
            },
            {
                field: 'distributor_name',
                headerName: t('Distributor Name'),
                flex: 1,
                minWidth: 100,
                renderCell: renderSimpleCell,
            },
            { field: 'zip_code', headerName: t('Zip code'), flex: 1, renderCell: renderSimpleCell },
            {
                field: 'member_number',
                headerName: t('No. of accounts'),
                flex: 1,
                minWidth: 120,
                renderCell: ({ row }) => (
                    <BaseTableColumn
                        columnValue={
                            row.store_overview?.store_members !== undefined &&
                            row.store_overview?.store_members !== null
                                ? String(row.store_overview.store_members)
                                : '-'
                        }
                    />
                ),
            },
            {
                field: 'store_address',
                headerName: t('Address'),
                flex: 1,
                minWidth: 150,
                renderCell: renderSimpleCell,
            },
            {
                field: 'store_location',
                type: 'actions',
                headerName: t('Location'),
                flex: 1,
                minWidth: 140,
                getActions: ({ row }) => [
                    row.store_location ? (
                        <GridActionsCellItem
                            icon={<LocationOn />}
                            label={t('Location')}
                            sx={{ color: 'primary.main' }}
                            onClick={() => {
                                const location = row.store_location as StoreLocation;
                                window.open(
                                    Array.isArray(location)
                                        ? `https://www.google.com/maps/search/?api=1&query=${location[0]},${location[1]}`
                                        : `https://www.google.com/maps/search/?api=1&query=${location?.latitude},${location?.longitude}`,
                                    '_blank',
                                );
                            }}
                        />
                    ) : (
                        <></>
                    ),
                ],
            },
            {
                field: 'action',
                type: 'actions',
                headerName: t('Action'),
                flex: 1,
                minWidth: 90,
                hideable: false,
                getActions: ({ id }) => {
                    return [
                        <GridActionsCellItem
                            icon={<Create />}
                            label={t('Edit')}
                            sx={{
                                color: 'primary.main',
                            }}
                            onClick={() => editItem(id)}
                        />,
                        <GridActionsCellItem
                            icon={<Delete />}
                            label={t('Delete')}
                            className="textPrimary"
                            onClick={() => deleteItem(id as number)}
                            color="inherit"
                        />,
                    ];
                },
            },
        ],
        [t, renderSimpleCell, editItem, deleteItem],
    );

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        store_id: false,
        store_name: true,
        store_code: true,
        store_cluster: true,
        distributor_name: false,
        zip_code: false,
        member_number: true,
        store_address: true,
        store_location: false,
        action: true,
    });

    const fetchStoreListHandler = async (pageParams = defaultPageParams) => {
        fetchAbortControllerRef.current?.abort();
        const fetchId = latestFetchIdRef.current + 1;
        latestFetchIdRef.current = fetchId;
        const abortController = new AbortController();
        fetchAbortControllerRef.current = abortController;
        dispatch({ type: 'SET_TABLE_LOADING', payload: true });
        const corporationId = (await localforage.getItem('corporation_id')) as number;
        const { field, sort } = sortModel;
        const params = {
            ...pageParams,
            cid: corporationId,
            keyword: searchQuery,
            sort_field: field,
            sort_order: sort,
            with_overview: 1,
        };
        try {
            const {
                data: { data, message, isSuccess },
            } = await fetchStoreListAPI(params, { signal: abortController.signal });

            if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;

            if (isSuccess) {
                dispatch({ type: 'SET_STORE_TABLE', payload: data });
                setValidatedMessage({});
            } else if (message === 'Validation failed.' && Array.isArray(data)) {
                const validatedMessageObj: { [key: string]: string | null } = {};
                data.forEach(({ field, message }) => {
                    validatedMessageObj[field] = message;
                });
                setValidatedMessage(validatedMessageObj);
                setNotification({ message: t('Validation failed.'), severity: 'error' });
            } else {
                setNotification({ message, severity: 'error' });
            }
        } catch (error) {
            if (abortController.signal.aborted) return;
            console.error('An unexpected error occurred:', error);
            setNotification({
                message: t('An unexpected error occurred'),
                severity: 'error',
            });
        } finally {
            if (latestFetchIdRef.current === fetchId) {
                dispatch({ type: 'SET_TABLE_LOADING', payload: false });
                fetchAbortControllerRef.current = null;
            }
        }
    };

    const pageChangeHandler = (pageModel: GridPaginationModel) => {
        dispatch({ type: 'SET_PAGINATION_MODEL', payload: pageModel });
        const pageParams = {
            current_page: pageModel.page + 1,
            data_per_page: pageModel.pageSize,
        };
        fetchStoreListHandler(pageParams);
    };

    const searchHandler = (value: string) => {
        const minLength = getInputMinLengthLimit(value);

        if (value.trim().length === 0 || value.length >= minLength) {
            setValidatedMessage({});
            dispatch({ type: 'SET_SEARCH_QUERY', payload: value });
        } else {
            setValidatedMessage({ keyword: t('Keyword length validation', { count: minLength }) });
        }
    };

    const sortModelChangeHandler = (sortModel: GridSortModel) => {
        if (sortModel.length > 0) {
            const { field, sort } = sortModel[0];
            dispatch({
                type: 'SET_SORT_MODEL',
                payload: { field: field as keyof StoreType, sort: sort as 'asc' | 'desc' },
            });
        } else {
            dispatch({
                type: 'SET_SORT_MODEL',
                payload: { field: null, sort: null },
            });
        }
    };

    useEffect(() => {
        fetchStoreListHandler();
    }, [searchQuery, sortModel]);

    useEffect(() => {
        return () => {
            fetchAbortControllerRef.current?.abort();
        };
    }, []);

    const handleConfirmDeleteStore = async () => {
        if (captchaKey !== captcha) {
            dispatch({ type: 'SET_IS_CAPTCHA_ERROR', payload: true });
            return;
        }

        try {
            const {
                data: { isSuccess, message },
            } = await deleteStoreConfirmAPI(deleteStoreId as number, { captcha });

            if (isSuccess) {
                setIsDeleteDialogOpen(false);
                setNotification({ message: message, severity: 'success' });
                setManageStoreId(null);
                fetchStoreListHandler();
            } else {
                setNotification({
                    message: message,
                    severity: 'error',
                });
            }
        } catch (error) {
            console.error('An unexpected error occurred:', error);
            setNotification({ message: t('An unexpected error occurred'), severity: 'error' });
        }

        setIsDeleteDialogOpen(false);
    };

    const closeDeleteDialogHandler = () => {
        setIsDeleteDialogOpen(false);
        dispatch({ type: 'CLEAR_CAPTCHA' });
    };

    return (
        <>
            <BasePaper className={'admin-paper'}>
                <BaseTitle icon={<RoomPreferences />} title={t('Store Admin')} />

                <BaseCard>
                    <div className="card-header">
                        <SearchBar
                            onSearch={searchHandler}
                            error={!!validatedMessage.keyword}
                            helperText={validatedMessage.keyword as string}
                            sx={{ my: 2 }}
                        ></SearchBar>
                        <Link to="/admin/store/new">
                            <BaseButton startIcon={<Create />}>{t('New Store')}</BaseButton>
                        </Link>
                    </div>
                    <BaseDataTable
                        rows={storeList}
                        columns={columns}
                        loading={tableLoading}
                        getRowId={getRowId}
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        paginationMode="server"
                        onPaginationModelChange={pageChangeHandler}
                        sortingMode="server"
                        onSortModelChange={sortModelChangeHandler}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={setColumnVisibilityModel}
                    />

                    <BaseDialog
                        dialogTitle={t('Delete Store')}
                        confirmButtonText={t('Confirm')}
                        cancelButtonText={t('Cancel')}
                        onConfirm={handleConfirmDeleteStore}
                        onClose={closeDeleteDialogHandler}
                        open={isDeleteDialogOpen}
                    >
                        <CaptchaCanvas
                            captchaKey={captchaKey}
                            isCaptchaError={isCaptchaError}
                            setCaptcha={(captcha) => dispatch({ type: 'SET_CAPTCHA', payload: captcha })}
                        />
                    </BaseDialog>
                </BaseCard>
            </BasePaper>
        </>
    );
}

export default AdminStorePage;
